import { isBannerflow } from '@studio/utils/url';
import { Event, EventHint, Breadcrumb, BreadcrumbHint } from '@sentry/types';
import { TrackerService } from './tracker.service';

/**
 * Decorator function that can be used to capture a single lifecycle methods of the component.
 */
export function TraceMethod(context?: string): any {
    return function (target: any, propertyKey: any, descriptor: any): any {
        const originalMethod = descriptor.value;
        const instanceName = context ? context : target.constructor.name;
        const trackerName = `${instanceName}.${String(propertyKey)}`;
        descriptor.value = async function (...args: any[]): Promise<any> {
            TrackerService.startTracking(trackerName);

            if (originalMethod) {
                const returnValue = await originalMethod.apply(this, args);

                TrackerService.finishTracking(trackerName);

                return returnValue;
            }
        };
        return descriptor;
    };
}

function isSentryEventFiltered(error: unknown): boolean {
    let stack: string | undefined;
    if (typeof error === 'string') {
        stack = error;
    } else if (
        error !== null &&
        typeof error === 'object' &&
        'stack' in error &&
        typeof error.stack === 'string'
    ) {
        stack = error.stack;
    }
    if (stack) {
        // remove widget related errors
        return stack.indexOf('widget-') > -1;
    }
    return false;
}

export function beforeSentrySend(event: Event, hint: EventHint): Event | null {
    const hasColorFormat = event.message?.includes('%c');
    if (hasColorFormat) {
        event.message = event.message!.replaceAll(/%c/g, ''); // filter out the custom formatted logger message
    }

    const isFilteredEvent = isSentryEventFiltered(hint.originalException);
    if (isFilteredEvent) {
        return null;
    }
    return event;
}

function getSentryBreadcrumbMessage(breadcrumb: Breadcrumb, hint?: BreadcrumbHint): string | undefined {
    // clean custom logger format
    if (breadcrumb.category === 'console') {
        if (breadcrumb.data && breadcrumb.data.arguments.length > 1) {
            return breadcrumb.data.arguments[0].replace(/%c/g, ''); // first argument is the console log
        }
    }

    // try to parse sentry breadcrumb for ui actions https://github.com/getsentry/sentry-javascript/issues/3015#issuecomment-718594200
    if (breadcrumb.category?.startsWith('ui')) {
        let target = hint?.event.target;
        if (target?.dataset?.sentry) {
            return `${target.tagName.toLowerCase()}[sentry="${target.dataset.sentry}"]`;
        }
        if (target && !target.id) {
            // try one level up, e.g. ui-buttons
            if (target.parentElement.id) {
                target = target.parentElement;
            }

            return `${target.tagName.toLowerCase()}#${target.id}`;
        }
    }

    return breadcrumb.message;
}

export function beforeSentryBreadcrumb(
    breadcrumb: Breadcrumb,
    hint?: BreadcrumbHint
): Breadcrumb | null {
    // filter external requests (e.g. gainsight ping)
    if (breadcrumb.category === 'xhr' && !isBannerflow(breadcrumb.data?.url)) {
        return null;
    }

    // filter debug logs
    if (breadcrumb.category === 'console' && breadcrumb.level === 'debug') {
        return null;
    }

    // don't breadcrumb selections
    if (breadcrumb.message?.match(/(workspace.*selection-0)/) !== null) {
        return null;
    }

    breadcrumb.message = getSentryBreadcrumbMessage(breadcrumb, hint);
    return breadcrumb;
}
